import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { useApp } from '@/contexts/AppContext';
import { generateReportPDF, downloadCSV } from '@/utils/pdfUtils';
import {
  demoCanteenBills,
  demoCanteenItems,
} from '@/mocks/demoData';

const COLORS = ['#1a2b4a', '#c9a84c', '#22c55e', '#ef4444', '#f59e0b', '#6b7280', '#8b5cf6', '#ec4899'];

export default function CanteenReports() {
  const { addToast } = useApp();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fromDate = dateFrom ? new Date(dateFrom) : new Date('2000-01-01');
  const toDate = dateTo ? new Date(dateTo) : new Date('2100-01-01');

  const billsInRange = useMemo(() => {
    return demoCanteenBills.filter((b) => {
      const d = new Date(b.created_at);
      return d >= fromDate && d <= toDate;
    });
  }, [fromDate, toDate]);

  // Daily Sales by Category
  const salesByCategory = useMemo(() => {
    const map = new Map<string, { qty: number; revenue: number; gst: number }>();
    billsInRange.forEach((bill) => {
      bill.items.forEach((item) => {
        const menuItem = demoCanteenItems.find((i) => i.id === item.item_id);
        const cat = menuItem?.category || 'Other';
        const curr = map.get(cat) || { qty: 0, revenue: 0, gst: 0 };
        curr.qty += item.qty;
        curr.revenue += item.price * item.qty;
        curr.gst += item.gst;
        map.set(cat, curr);
      });
    });
    return Array.from(map.entries()).map(([name, data]) => ({ name, ...data })).sort((a, b) => b.revenue - a.revenue);
  }, [billsInRange]);

  // Top Selling Items
  const topItems = useMemo(() => {
    const map = new Map<string, { name: string; qty: number; revenue: number }>();
    billsInRange.forEach((bill) => {
      bill.items.forEach((item) => {
        const curr = map.get(item.item_id) || { name: item.name, qty: 0, revenue: 0 };
        curr.qty += item.qty;
        curr.revenue += item.price * item.qty;
        map.set(item.item_id, curr);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.qty - a.qty).slice(0, 8);
  }, [billsInRange]);

  // Revenue Trend (daily)
  const revenueTrend = useMemo(() => {
    const map = new Map<string, number>();
    billsInRange.filter((b) => b.status === 'paid').forEach((b) => {
      const d = new Date(b.created_at).toISOString().slice(0, 10);
      map.set(d, (map.get(d) || 0) + b.total);
    });
    return Array.from(map.entries()).map(([date, total]) => ({ date, total })).sort((a, b) => a.date.localeCompare(b.date));
  }, [billsInRange]);

  // GST Collection
  const gstTotal = useMemo(() => {
    return billsInRange.reduce((s, b) => s + b.gst_total, 0);
  }, [billsInRange]);

  // Pending Bills
  const pendingBills = useMemo(() => {
    return billsInRange.filter((b) => b.status === 'pending');
  }, [billsInRange]);

  const handleGeneratePDF = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; dataKey: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data available for this report');
      return;
    }
    generateReportPDF(title, cols, rows, dateFrom && dateTo ? `Period: ${dateFrom} to ${dateTo}` : undefined);
    addToast('success', `${title} PDF downloaded`);
  };

  const handleDownloadCSV = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; key: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data available for this report');
      return;
    }
    downloadCSV(title.replace(/\s+/g, '-'), cols, rows);
    addToast('success', `${title} CSV downloaded`);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Canteen Reports</h1>
        <p className="text-navy-500 mt-1">Sales, GST, and top-selling item analytics.</p>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Sales by Category */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Sales by Category</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Daily Sales', () => salesByCategory.map((c) => ({ category: c.name, qty: c.qty, revenue: c.revenue, gst: c.gst })), () => [{ header: 'Category', dataKey: 'category' }, { header: 'Qty Sold', dataKey: 'qty' }, { header: 'Revenue', dataKey: 'revenue' }, { header: 'GST', dataKey: 'gst' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Daily Sales', () => salesByCategory.map((c) => ({ category: c.name, qty: c.qty, revenue: c.revenue, gst: c.gst })), () => [{ header: 'Category', key: 'category' }, { header: 'Qty Sold', key: 'qty' }, { header: 'Revenue', key: 'revenue' }, { header: 'GST', key: 'gst' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={salesByCategory} cx="50%" cy="50%" outerRadius={70} paddingAngle={3} dataKey="revenue" label={({ name, revenue }) => `${name}: Rs.${revenue}`}>
                  {salesByCategory.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => `Rs. ${v.toLocaleString('en-IN')}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Selling Items */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Top Selling Items</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Top Selling Items', () => topItems.map((i) => ({ item: i.name, qty: i.qty, revenue: i.revenue })), () => [{ header: 'Item', dataKey: 'item' }, { header: 'Qty Sold', dataKey: 'qty' }, { header: 'Revenue', dataKey: 'revenue' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Top Selling Items', () => topItems.map((i) => ({ item: i.name, qty: i.qty, revenue: i.revenue })), () => [{ header: 'Item', key: 'item' }, { header: 'Qty Sold', key: 'qty' }, { header: 'Revenue', key: 'revenue' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topItems} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={100} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="qty" fill="#c9a84c" radius={[0, 4, 4, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* GST Collection */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">GST Collection</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('GST Collection', () => [{ totalGst: gstTotal, totalBills: billsInRange.length, avgGst: billsInRange.length > 0 ? Math.round(gstTotal / billsInRange.length) : 0 }], () => [{ header: 'Total GST', dataKey: 'totalGst' }, { header: 'Total Bills', dataKey: 'totalBills' }, { header: 'Avg GST/Bill', dataKey: 'avgGst' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('GST Collection', () => [{ totalGst: gstTotal, totalBills: billsInRange.length, avgGst: billsInRange.length > 0 ? Math.round(gstTotal / billsInRange.length) : 0 }], () => [{ header: 'Total GST', key: 'totalGst' }, { header: 'Total Bills', key: 'totalBills' }, { header: 'Avg GST/Bill', key: 'avgGst' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center h-48">
            <div className="text-4xl font-bold text-navy-800">Rs. {gstTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
            <div className="text-sm text-navy-500 mt-2">Total GST Collected</div>
            <div className="text-xs text-navy-400 mt-4">Across {billsInRange.length} bills</div>
            <div className="text-xs text-navy-400">Avg Rs. {billsInRange.length > 0 ? Math.round(gstTotal / billsInRange.length) : 0} per bill</div>
          </div>
        </div>
      </div>

      {/* Revenue Trend */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Revenue Trend</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Revenue Trend', () => revenueTrend.map((d) => ({ date: d.date, total: d.total })), () => [{ header: 'Date', dataKey: 'date' }, { header: 'Revenue', dataKey: 'total' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Revenue Trend', () => revenueTrend.map((d) => ({ date: d.date, total: d.total })), () => [{ header: 'Date', key: 'date' }, { header: 'Revenue', key: 'total' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => [`Rs. ${v.toLocaleString('en-IN')}`, 'Revenue']} />
              <Line type="monotone" dataKey="total" stroke="#1a2b4a" strokeWidth={2} dot={{ r: 3, fill: '#c9a84c' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pending Bills */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Pending Bills ({pendingBills.length})</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Pending Bills', () => pendingBills.map((b) => ({ bill: b.bill_no, location: b.table_or_room, total: b.total, payment: b.payment_method })), () => [{ header: 'Bill No', dataKey: 'bill' }, { header: 'Table/Room', dataKey: 'location' }, { header: 'Total', dataKey: 'total' }, { header: 'Payment Method', dataKey: 'payment' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Pending Bills', () => pendingBills.map((b) => ({ bill: b.bill_no, location: b.table_or_room, total: b.total, payment: b.payment_method })), () => [{ header: 'Bill No', key: 'bill' }, { header: 'Table/Room', key: 'location' }, { header: 'Total', key: 'total' }, { header: 'Payment Method', key: 'payment' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        {pendingBills.length === 0 ? (
          <div className="text-center py-8 text-navy-400 text-sm">No pending bills in selected range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-50">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-navy-700">Bill No</th>
                  <th className="text-left px-4 py-2 font-semibold text-navy-700">Table/Room</th>
                  <th className="text-left px-4 py-2 font-semibold text-navy-700">Items</th>
                  <th className="text-right px-4 py-2 font-semibold text-navy-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {pendingBills.map((b) => (
                  <tr key={b.id} className="border-t border-navy-100 hover:bg-navy-50/50">
                    <td className="px-4 py-2.5 text-navy-700 text-xs font-medium">{b.bill_no}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs">{b.table_or_room}</td>
                    <td className="px-4 py-2.5 text-navy-500 text-xs">{b.items.length} items</td>
                    <td className="px-4 py-2.5 text-navy-700 text-xs text-right font-medium">Rs. {b.total.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{billsInRange.length}</div>
          <div className="text-xs text-navy-500 mt-1">Total Bills</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">Rs. {billsInRange.filter((b) => b.status === 'paid').reduce((s, b) => s + b.total, 0).toLocaleString('en-IN')}</div>
          <div className="text-xs text-navy-500 mt-1">Paid Revenue</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-red-600">Rs. {pendingBills.reduce((s, b) => s + b.total, 0).toLocaleString('en-IN')}</div>
          <div className="text-xs text-navy-500 mt-1">Pending Amount</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoCanteenItems.filter((i) => i.available).length}</div>
          <div className="text-xs text-navy-500 mt-1">Menu Items</div>
        </div>
      </div>
    </div>
  );
}